import CampaignIcon from "@mui/icons-material/Campaign";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import DraftsIcon from "@mui/icons-material/Drafts";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import GroupIcon from "@mui/icons-material/Group";
import PaymentsIcon from "@mui/icons-material/Payments";
import RedeemIcon from "@mui/icons-material/Redeem";
import SellIcon from "@mui/icons-material/Sell";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import type React from "react";
import { useState } from "react";
import { formatUsd } from "@/utils/number-format";
import OutreachList from "../../components/gfgAdmin/OutreachList";
import ThankYouFeed from "../../components/gfgAdmin/ThankYouFeed";
import { GfgSelect } from "../../components/ui/GfgSelect";
import { useAdminOverview } from "../../hooks/useAdminOverview";

const DATE_RANGE_OPTIONS = [
  { value: "1", label: "Last 24 hours" },
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
  { value: "180", label: "Last 6 months" },
  { value: "365", label: "Last 12 months" },
  { value: "0", label: "All time" },
];

// ─── Stat Card ───────────────────────────────────────────────────────────────
function StatCard({
  label,
  icon,
  value,
  sub,
  danger = false,
}: {
  label: string;
  icon: React.ReactNode;
  value: string | number;
  sub: string;
  danger?: boolean;
}) {
  return (
    <div className="bg-white rounded-[10px] p-[20px] border border-smoke">
      <div className="flex items-center justify-between mb-[4px]">
        <span className="text-[11px] font-bold text-rodeo-dust uppercase tracking-[2px]">
          {label}
        </span>
        <span className="text-[22px] text-rodeo-dust">{icon}</span>
      </div>
      <div
        className={`font-crimson text-[42px] font-normal leading-[1] mb-[2px] ${danger ? "text-rodeo-black" : "text-rodeo-black"}`}
      >
        {value}
      </div>
      <div className="text-[14px] text-graphite">{sub}</div>
    </div>
  );
}

// ─── Overview Tab (Main Export) ───────────────────────────────────────────────
export default function OverviewTab() {
  const [range, setRange] = useState(90);
  const { data: stats, isLoading, error } = useAdminOverview(range);

  return (
    <div className="pb-12">
      {/* Controls header */}
      <div className="flex items-end justify-between gap-4 flex-wrap mb-5">
        <div>
          <div className="font-crimson text-[24px] text-rodeo-black leading-[1.1]">
            Performance overview
          </div>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          {/* Date range field */}
          <div className="min-w-[150px]">
            <GfgSelect
              label="Date range"
              value={String(range)}
              onChange={(e) => setRange(Number(e.target.value))}
              options={DATE_RANGE_OPTIONS}
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-7">
          <div className="animate-pulse h-[120px] bg-smoke rounded-[10px]" />
          <div className="animate-pulse h-[120px] bg-smoke rounded-[10px]" />
        </div>
      ) : error ? (
        <div className="p-4 text-good-wine bg-good-wine-bg rounded-lg border border-good-wine-4">
          Error loading overview stats.
        </div>
      ) : stats ? (
        <div className="flex flex-col gap-7">
          {/* Platform performance group */}
          <div>
            <div className="text-[12px] font-bold uppercase tracking-[1px] text-rodeo-black mb-3">
              Platform
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              <StatCard
                label="Active Users"
                icon={<GroupIcon />}
                value={stats.platform.activeUsers.toLocaleString()}
                sub="signed in this period"
              />
              <StatCard
                label="Campaigns Sent"
                icon={<CampaignIcon />}
                value={stats.platform.campaignsSent.total.toLocaleString()}
                sub={`${stats.platform.campaignsSent.bulk} bulk · ${stats.platform.campaignsSent.automated} automated`}
              />
              <StatCard
                label="Credits Purchased"
                icon={<PaymentsIcon />}
                value={formatUsd(stats.platform.creditsPurchased.total || 0)}
                sub={`${stats.platform.creditsPurchased.transactions} transactions`}
              />
              <StatCard
                label="Credits to Charity"
                icon={<VolunteerActivismIcon />}
                value={formatUsd(stats.platform.creditsToCharity.total || 0)}
                sub={`${stats.platform.creditsToCharity.allocations} allocations`}
              />
            </div>
          </div>

          {/* Gifting performance group */}
          <div>
            <div className="text-[12px] font-bold uppercase tracking-[1px] text-rodeo-black mb-3">
              Gifting performance
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              <StatCard
                label="Gifts Delivered"
                icon={<CardGiftcardIcon />}
                value={stats.gifting.giftsDelivered.total.toLocaleString()}
                sub={`across ${stats.gifting.giftsDelivered.campaigns} campaigns`}
              />
              <StatCard
                label="Redemption Rate"
                icon={<RedeemIcon />}
                value={`${stats.gifting.redemptionRate.percentage}%`}
                sub={`${stats.gifting.redemptionRate.redeemed.toLocaleString()} of ${stats.gifting.redemptionRate.delivered.toLocaleString()} redeemed`}
              />
              <StatCard
                label="Redemption Value"
                icon={<SellIcon />}
                value={formatUsd(stats.gifting.redemptionValue || 0)}
                sub="value of gifts redeemed"
              />
              <StatCard
                label="Open Rate"
                icon={<DraftsIcon />}
                value={`${stats.gifting.openRate}%`}
                sub="weighted by recipients"
              />
              <StatCard
                label="Thank-You Messages"
                icon={<FavoriteBorderIcon />}
                value={stats.gifting.thankYouMessages.toLocaleString()}
                sub="sent back by recipients"
              />
              <StatCard
                label="Unresolved Bounces"
                icon={<ErrorOutlineIcon />}
                value={stats.gifting.unresolvedBounces.toLocaleString()}
                sub={stats.gifting.unresolvedBounces > 0 ? "awaiting a fix" : "all clear"}
                danger={stats.gifting.unresolvedBounces > 0}
              />
            </div>
          </div>

          <OutreachList />
          <ThankYouFeed />
        </div>
      ) : null}
    </div>
  );
}
